<template>
    <div class="analytics-dashboard container-fluid py-4">
        <!-- Header -->
        <div class="row mb-4">
            <div class="col-12">
                <nav class="breadcrumb-nav mb-2">
                    <a href="/analytics" class="text-muted">Analytics</a>
                    <span class="mx-2 text-muted">/</span>
                    <a href="/analytics/defi" class="text-muted">DeFi</a>
                    <span class="mx-2 text-muted">/</span>
                    <span class="text-gradient">{{ dexName }}</span>
                </nav>
                <div class="d-flex align-items-center gap-3 mb-2">
                    <img v-if="resolvedDexLogo" :src="resolvedDexLogo" :alt="dexName" class="dex-logo" @error="$event.target.style.display='none'" />
                    <h1 class="mb-0">
                        <span class="text-gradient">{{ dexName }} Analytics</span>
                    </h1>
                </div>
                <div class="d-flex flex-wrap gap-2 mb-3" v-if="dexData.programs && dexData.programs.length">
                    <span v-for="p in dexData.programs" :key="p.address" class="badge-program">
                        <a :href="`/analytics/programs/${p.address}`" class="font-mono text-muted">
                            {{ p.name || formatPubkey(p.address) }}
                        </a>
                    </span>
                </div>
                <date-range-picker default-range="24h" @change="handleDateRangeChange" />
            </div>
        </div>

        <!-- Hero Stats -->
        <div class="analytics-hero mb-4" v-if="!loading">
            <div class="analytics-hero-stat">
                <div class="hero-label">Volume</div>
                <div class="hero-value">${{ formatCompact(summary.totalVolume) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--green">
                <div class="hero-label">Market Share</div>
                <div class="hero-value">{{ summary.marketShare || 0 }}%</div>
            </div>
            <div class="analytics-hero-stat hero-stat--cyan">
                <div class="hero-label">Trades</div>
                <div class="hero-value">{{ formatCompact(summary.totalTrades) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--blue">
                <div class="hero-label">Unique Traders</div>
                <div class="hero-value">{{ formatCompact(summary.totalTraders) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--amber">
                <div class="hero-label">Active Markets</div>
                <div class="hero-value">{{ formatCompact(summary.totalMarkets) }}</div>
            </div>
        </div>
        <div v-if="loading" class="text-center text-muted py-5">Loading DEX data...</div>

        <!-- SECTION: Volume & Market Share -->
        <div class="section-divider">
            <span class="section-title">Volume & Market Share</span>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Volume Over Time</div>
                    <div class="panel-subtitle">{{ dexName }} volume with network total overlay</div>
                </div>
                <div class="panel-body">
                    <canvas ref="volumeChart" height="280"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Market Share Trend</div>
                    <div class="panel-subtitle">% of total DEX volume on Solana</div>
                </div>
                <div class="panel-body">
                    <canvas ref="marketShareChart" height="280"></canvas>
                </div>
            </div>
        </div>

        <!-- SECTION: Top Traded Tokens -->
        <div class="section-divider">
            <span class="section-title">Top Traded Tokens</span>
        </div>
        <div class="analytics-row cols-1 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Most Traded Tokens</div>
                    <div class="panel-subtitle">Ranked by volume on {{ dexName }}</div>
                </div>
                <div class="panel-body">
                    <table class="compass-table compass-table--striped">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Token</th>
                                <th class="text-end">Volume</th>
                                <th class="text-end">Trades</th>
                                <th class="text-end">Traders</th>
                                <th class="text-end">Buy/Sell</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(t, i) in tokens" :key="t.token">
                                <td class="text-muted">{{ i + 1 }}</td>
                                <td>
                                    <a :href="`/tokens/${t.token}`" class="d-flex align-items-center gap-2 text-decoration-none">
                                        <img v-if="t.image" :src="t.image" class="token-logo" @error="$event.target.style.display='none'" />
                                        <span>{{ t.symbol || formatPubkey(t.token) }}</span>
                                    </a>
                                </td>
                                <td class="text-end td-num">${{ formatCompact(t.volume) }}</td>
                                <td class="text-end td-num">{{ formatCompact(t.trades) }}</td>
                                <td class="text-end td-num">{{ formatCompact(t.traders) }}</td>
                                <td class="text-end">
                                    <div class="buy-sell-bar">
                                        <div class="buy-segment" :style="{ width: (t.buySellRatio * 100) + '%' }"></div>
                                    </div>
                                    <small class="text-muted">{{ (t.buySellRatio * 100).toFixed(0) }}% buy</small>
                                </td>
                            </tr>
                            <tr v-if="!tokens.length">
                                <td colspan="6" class="text-center text-muted py-4">No token data available</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- SECTION: Top Pools -->
        <div class="section-divider" v-if="pools.length">
            <span class="section-title">Top Pools</span>
        </div>
        <div class="analytics-row cols-1 mb-4" v-if="pools.length">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Pools by TVL</div>
                    <div class="panel-subtitle">Top liquidity pools on {{ dexName }}</div>
                </div>
                <div class="panel-body">
                    <table class="compass-table compass-table--striped">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Pool</th>
                                <th class="text-end">TVL</th>
                                <th class="text-end">Fee Tier</th>
                                <th class="text-end">Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(p, i) in pools" :key="p.address || i">
                                <td class="text-muted">{{ i + 1 }}</td>
                                <td>
                                    <div class="d-flex align-items-center gap-2">
                                        <div class="pool-pair-logos">
                                            <img v-if="p.tokenA && p.tokenA.image" :src="p.tokenA.image" class="token-logo" @error="$event.target.style.display='none'" />
                                            <img v-if="p.tokenB && p.tokenB.image" :src="p.tokenB.image" class="token-logo token-logo--overlap" @error="$event.target.style.display='none'" />
                                        </div>
                                        <span>{{ poolPairLabel(p) }}</span>
                                    </div>
                                </td>
                                <td class="text-end td-num">${{ formatCompact(p.tvl) }}</td>
                                <td class="text-end td-num">{{ p.feeBps != null ? (p.feeBps / 100).toFixed(2) + '%' : '—' }}</td>
                                <td class="text-end td-num text-muted">{{ p.poolType || '—' }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- SECTION: Trade Size Distribution -->
        <div class="section-divider">
            <span class="section-title">Trade Size Distribution</span>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Whale vs Retail</div>
                    <div class="panel-subtitle">Volume distribution by trade size on {{ dexName }}</div>
                </div>
                <div class="panel-body">
                    <canvas ref="tradeSizeChart" height="280"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Retail / Whale Split</div>
                    <div class="panel-subtitle">Aggregate volume breakdown</div>
                </div>
                <div class="panel-body d-flex flex-column justify-content-center" style="min-height: 280px;">
                    <div class="whale-retail-split" v-if="tradeSize.retailPercent !== undefined">
                        <div class="split-bar mb-3">
                            <div class="split-segment split-segment--retail" :style="{ width: tradeSize.retailPercent + '%' }">
                                <span v-if="tradeSize.retailPercent > 15">Retail {{ tradeSize.retailPercent }}%</span>
                            </div>
                            <div class="split-segment split-segment--whale" :style="{ width: tradeSize.whalePercent + '%' }">
                                <span v-if="tradeSize.whalePercent > 15">Whale {{ tradeSize.whalePercent }}%</span>
                            </div>
                        </div>
                        <div class="d-flex justify-content-between">
                            <div>
                                <div class="text-muted small">Retail Volume</div>
                                <div class="font-mono">${{ formatCompact(tradeSize.retailVolume) }}</div>
                            </div>
                            <div class="text-end">
                                <div class="text-muted small">Whale Volume</div>
                                <div class="font-mono">${{ formatCompact(tradeSize.whaleVolume) }}</div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="text-center text-muted">No trade size data</div>
                </div>
            </div>
        </div>

        <!-- SECTION: Aggregator Share -->
        <div class="section-divider">
            <span class="section-title">Aggregator Share</span>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Direct vs Aggregated</div>
                    <div class="panel-subtitle">% of volume from Jupiter and other aggregators</div>
                </div>
                <div class="panel-body">
                    <canvas ref="aggregatorChart" height="280"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Top Aggregators</div>
                    <div class="panel-subtitle">Volume routed through aggregators to {{ dexName }}</div>
                </div>
                <div class="panel-body">
                    <table class="compass-table compass-table--striped">
                        <thead>
                            <tr>
                                <th>Aggregator</th>
                                <th class="text-end">Volume</th>
                                <th class="text-end">Share</th>
                                <th class="text-end">Trades</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="a in aggregator.aggregators" :key="a.program">
                                <td>
                                    <div class="d-flex align-items-center gap-2">
                                        <img v-if="a.logo" :src="a.logo" class="agg-logo" @error="$event.target.style.display='none'" />
                                        <span>{{ a.name || formatPubkey(a.program) }}</span>
                                    </div>
                                </td>
                                <td class="text-end td-num">${{ formatCompact(a.volume) }}</td>
                                <td class="text-end td-num">{{ a.percent }}%</td>
                                <td class="text-end td-num">{{ formatCompact(a.trades) }}</td>
                            </tr>
                            <tr v-if="!aggregator.aggregators || !aggregator.aggregators.length">
                                <td colspan="4" class="text-center text-muted py-3">No aggregator data</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- SECTION: Instruction Profiling -->
        <div class="section-divider">
            <span class="section-title">Instruction Profiling</span>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Top Instructions</div>
                    <div class="panel-subtitle">Most-called discriminators and their compute usage</div>
                </div>
                <div class="panel-body">
                    <canvas ref="instructionChart" height="320"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Instruction Details</div>
                    <div class="panel-subtitle">CU per instruction, CPI depth</div>
                </div>
                <div class="panel-body">
                    <table class="compass-table compass-table--striped">
                        <thead>
                            <tr>
                                <th>Discriminator</th>
                                <th class="text-end">Count</th>
                                <th class="text-end">Avg CU</th>
                                <th class="text-end">CPI Depth</th>
                                <th class="text-end">Share</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="ix in instructionData.instructions" :key="ix.discriminator">
                                <td class="font-mono">{{ ix.discriminator }}</td>
                                <td class="text-end td-num">{{ formatCompact(ix.count) }}</td>
                                <td class="text-end td-num">{{ formatCompact(ix.avgCU) }}</td>
                                <td class="text-end td-num">{{ ix.avgCpiDepth }}</td>
                                <td class="text-end td-num">{{ ix.percent }}%</td>
                            </tr>
                            <tr v-if="!instructionData.instructions || !instructionData.instructions.length">
                                <td colspan="5" class="text-center text-muted py-3">No instruction data</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- SECTION: Fee Economics -->
        <div class="section-divider">
            <span class="section-title">Fee Economics</span>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Fee Breakdown Over Time</div>
                    <div class="panel-subtitle">Base fees, priority fees, and Jito tips</div>
                </div>
                <div class="panel-body">
                    <canvas ref="feeChart" height="280"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Fee Summary</div>
                    <div class="panel-subtitle">Total protocol fees and averages</div>
                </div>
                <div class="panel-body d-flex flex-column justify-content-center" style="min-height: 280px;">
                    <div class="fee-summary-grid" v-if="feeSummary.totalFees !== undefined">
                        <div class="fee-summary-item">
                            <div class="text-muted small">Total Fees</div>
                            <div class="font-mono fee-value">{{ formatSOL(feeSummary.totalFees) }}</div>
                        </div>
                        <div class="fee-summary-item">
                            <div class="text-muted small">Priority Fees</div>
                            <div class="font-mono fee-value" style="color: var(--compass-purple)">{{ formatSOL(feeSummary.totalPriorityFees) }}</div>
                        </div>
                        <div class="fee-summary-item">
                            <div class="text-muted small">Jito Tips</div>
                            <div class="font-mono fee-value" style="color: #f97316">{{ formatSOL(feeSummary.totalTips) }}</div>
                        </div>
                        <div class="fee-summary-item">
                            <div class="text-muted small">Avg Fee/Trade</div>
                            <div class="font-mono fee-value">{{ formatFeeAvg(feeSummary.avgFeePerSwap) }}</div>
                        </div>
                        <div class="fee-summary-item">
                            <div class="text-muted small">Total Transactions</div>
                            <div class="font-mono fee-value">{{ formatCompact(feeSummary.totalTransactions) }}</div>
                        </div>
                        <div class="fee-summary-item">
                            <div class="text-muted small">Total Revenue</div>
                            <div class="font-mono fee-value" style="color: var(--compass-cyan)">{{ formatSOL(feeSummary.totalRevenue) }}</div>
                        </div>
                    </div>
                    <div v-else class="text-center text-muted">Loading fee data...</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { markRaw } from 'vue';
import Chart from 'chart.js/auto';
import { formatCompact, formatCompute, formatSOL, formatUSD, formatPubkey } from '../composables/useFormatters';
import { colors, getLineChartOptions, getBarChartOptions, getStackedBarChartOptions } from '../composables/useChartConfig';

export default {
    name: 'DexDetail',
    props: {
        slug: { type: String, required: true },
        dexName: { type: String, required: true },
        dexLogo: { type: String, default: '' },
    },

    data() {
        return {
            loading: true,
            resolvedDexLogo: this.dexLogo || '',
            currentRange: '24h',
            currentInterval: '1h',
            summary: {},
            timeSeries: [],
            networkTimeSeries: [],
            tokens: [],
            pools: [],
            tradeSize: {},
            aggregator: { aggregators: [] },
            instructionData: { instructions: [], summary: {} },
            feeSummary: {},
            feeTimeSeries: [],
            dexData: { programs: [] },
            refreshInterval: null,
            charts: {},
            colors: colors,
        };
    },

    mounted() {
        this.initCharts();
        this.loadData();
        this.refreshInterval = setInterval(() => this.loadData(), 60000);
    },

    beforeUnmount() {
        Object.values(this.charts).forEach(c => c?.destroy());
        if (this.refreshInterval) clearInterval(this.refreshInterval);
    },

    methods: {
        formatCompact,
        formatCompute,
        formatSOL,
        formatUSD,
        formatPubkey,

        formatFeeAvg(v) {
            if (!v) return '0 SOL';
            if (v >= 1) return v.toFixed(2) + ' SOL';
            if (v >= 0.001) return v.toFixed(4) + ' SOL';
            return v.toFixed(6) + ' SOL';
        },

        poolPairLabel(p) {
            const a = (p.tokenA && p.tokenA.symbol) ? p.tokenA.symbol : formatPubkey(p.tokenA?.mint || '');
            const b = (p.tokenB && p.tokenB.symbol) ? p.tokenB.symbol : formatPubkey(p.tokenB?.mint || '');
            return `${a} / ${b}`;
        },

        handleDateRangeChange(data) {
            this.currentRange = data.range;
            this.currentInterval = data.interval;
            this.loadData();
        },

        async loadData() {
            this.loading = true;
            try {
                const [detailRes, instrRes, feeRes] = await Promise.all([
                    fetch(`/analytics/api/dex-detail/${this.slug}?range=${this.currentRange}&interval=${this.currentInterval}`),
                    fetch(`/analytics/api/dex-instructions/${this.slug}?range=${this.currentRange}&interval=${this.currentInterval}&limit=20`),
                    fetch(`/analytics/api/dex-fees/${this.slug}?range=${this.currentRange}&interval=${this.currentInterval}`),
                ]);

                const data = await detailRes.json();
                this.summary = data.summary || {};
                this.timeSeries = data.timeSeries || [];
                this.networkTimeSeries = data.networkTimeSeries || [];
                this.tokens = data.tokens || [];
                this.pools = data.pools || [];
                this.tradeSize = data.tradeSize || {};
                this.aggregator = data.aggregator || { aggregators: [] };
                this.dexData = data.dex || { programs: [] };
                if (data.dex?.logo) this.resolvedDexLogo = data.dex.logo;

                try {
                    const instrData = await instrRes.json();
                    this.instructionData = instrData || { instructions: [], summary: {} };
                } catch (e) {
                    this.instructionData = { instructions: [], summary: {} };
                }

                try {
                    const feeData = await feeRes.json();
                    this.feeSummary = feeData.summary || {};
                    this.feeTimeSeries = feeData.timeSeries || [];
                } catch (e) {
                    this.feeSummary = {};
                    this.feeTimeSeries = [];
                }

                this.updateCharts();
            } catch (e) {
                console.error('DexDetail loadData error:', e);
            } finally {
                this.loading = false;
            }
        },

        initCharts() {
            // Volume chart (bar + line overlay for network)
            this.charts.volume = markRaw(new Chart(this.$refs.volumeChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: {
                    ...getBarChartOptions({ formatCallback: v => '$' + formatCompact(v) }),
                    plugins: {
                        ...getBarChartOptions({}).plugins,
                        legend: {
                            labels: { color: '#9ca3af', font: { family: "'JetBrains Mono', monospace" } }
                        }
                    }
                }
            }));

            // Market share chart
            this.charts.marketShare = markRaw(new Chart(this.$refs.marketShareChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: getLineChartOptions({
                    formatCallback: v => v.toFixed(1) + '%',
                    gradient: true
                })
            }));

            // Trade size distribution (horizontal bar)
            this.charts.tradeSize = markRaw(new Chart(this.$refs.tradeSizeChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: {
                    ...getBarChartOptions({ formatCallback: v => '$' + formatCompact(v) }),
                    indexAxis: 'y',
                    plugins: {
                        ...getBarChartOptions({}).plugins,
                        legend: { display: false }
                    },
                    scales: {
                        x: {
                            ticks: { color: '#9ca3af', callback: v => '$' + formatCompact(v) },
                            grid: { color: 'rgba(255,255,255,0.05)' }
                        },
                        y: {
                            ticks: { color: '#9ca3af', font: { family: "'JetBrains Mono', monospace", size: 11 } },
                            grid: { display: false }
                        }
                    }
                }
            }));

            // Aggregator doughnut
            this.charts.aggregator = markRaw(new Chart(this.$refs.aggregatorChart, {
                type: 'doughnut',
                data: { labels: [], datasets: [] },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            position: 'bottom',
                            labels: { color: '#9ca3af', padding: 16, font: { size: 12 } }
                        },
                        tooltip: {
                            backgroundColor: 'rgba(17, 17, 17, 0.95)',
                            titleColor: '#fff',
                            bodyColor: '#e5e7eb',
                            borderColor: 'rgba(139, 92, 246, 0.3)',
                            borderWidth: 1,
                            callbacks: {
                                label: (ctx) => {
                                    const v = ctx.raw || 0;
                                    return ` $${formatCompact(v)}`;
                                }
                            }
                        }
                    }
                }
            }));

            // Instruction profiling (horizontal bar)
            this.charts.instructions = markRaw(new Chart(this.$refs.instructionChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: {
                    ...getBarChartOptions({ formatCallback: v => formatCompact(v) }),
                    indexAxis: 'y',
                    scales: {
                        x: {
                            ticks: { color: '#9ca3af', callback: v => formatCompact(v) },
                            grid: { color: 'rgba(255,255,255,0.05)' }
                        },
                        y: {
                            ticks: { color: '#9ca3af', font: { family: "'JetBrains Mono', monospace", size: 11 } },
                            grid: { display: false }
                        }
                    }
                }
            }));

            // Fee breakdown (stacked bar)
            this.charts.fees = markRaw(new Chart(this.$refs.feeChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getStackedBarChartOptions({
                    formatCallback: v => formatSOL(v)
                })
            }));
        },

        updateCharts() {
            this.updateVolumeChart();
            this.updateMarketShareChart();
            this.updateTradeSizeChart();
            this.updateAggregatorChart();
            this.updateInstructionChart();
            this.updateFeeChart();
        },

        updateVolumeChart() {
            const ts = this.timeSeries;
            if (!ts.length) return;
            const labels = ts.map(d => d.label);

            // Build network volume lookup
            const networkMap = {};
            (this.networkTimeSeries || []).forEach(d => { networkMap[d.timestamp] = d.volume; });

            this.charts.volume.data = {
                labels,
                datasets: [
                    {
                        label: this.dexName + ' Volume',
                        data: ts.map(d => d.volume),
                        backgroundColor: this.colors.primary,
                        borderWidth: 0,
                        order: 2,
                    },
                    {
                        label: 'Network Total',
                        data: ts.map(d => networkMap[d.timestamp] || 0),
                        type: 'line',
                        borderColor: 'rgba(156, 163, 175, 0.5)',
                        backgroundColor: 'transparent',
                        borderWidth: 1.5,
                        borderDash: [4, 4],
                        pointRadius: 0,
                        tension: 0.4,
                        order: 1,
                    }
                ]
            };
            this.charts.volume.update();
        },

        updateMarketShareChart() {
            const ts = this.timeSeries;
            const nts = this.networkTimeSeries;
            if (!ts.length || !nts.length) return;

            const networkMap = {};
            nts.forEach(d => { networkMap[d.timestamp] = d.volume; });

            const labels = ts.map(d => d.label);
            const shareData = ts.map(d => {
                const net = networkMap[d.timestamp] || 0;
                return net > 0 ? (d.volume / net) * 100 : 0;
            });

            this.charts.marketShare.data = {
                labels,
                datasets: [{
                    label: 'Market Share %',
                    data: shareData,
                    borderColor: this.colors.accent,
                    backgroundColor: 'rgba(16, 247, 207, 0.1)',
                    fill: true,
                    borderWidth: 2,
                    tension: 0.4,
                }]
            };
            this.charts.marketShare.update();
        },

        updateTradeSizeChart() {
            const buckets = this.tradeSize.buckets || [];
            if (!buckets.length) return;

            this.charts.tradeSize.data = {
                labels: buckets.map(b => b.label),
                datasets: [{
                    label: 'Volume',
                    data: buckets.map(b => b.volume),
                    backgroundColor: [
                        '#6366f1', '#8b5cf6', '#a855f7', '#f59e0b', '#ef4444'
                    ],
                    borderWidth: 0,
                }]
            };
            this.charts.tradeSize.update();
        },

        updateAggregatorChart() {
            const agg = this.aggregator;
            if (!agg) return;

            const aggLabels = ['Direct'];
            const aggData = [agg.directVolume || 0];
            const aggColors = [this.colors.primary];

            (agg.aggregators || []).forEach((a, i) => {
                aggLabels.push(a.name || formatPubkey(a.program));
                aggData.push(a.volume);
                aggColors.push(this.colors.series[(i + 1) % this.colors.series.length]);
            });

            this.charts.aggregator.data = {
                labels: aggLabels,
                datasets: [{
                    data: aggData,
                    backgroundColor: aggColors,
                    borderWidth: 0,
                }]
            };
            this.charts.aggregator.update();
        },

        updateInstructionChart() {
            const instructions = this.instructionData.instructions || [];
            if (!instructions.length) return;

            const top10 = instructions.slice(0, 10);

            this.charts.instructions.data = {
                labels: top10.map(ix => ix.discriminator),
                datasets: [
                    {
                        label: 'Count',
                        data: top10.map(ix => ix.count),
                        backgroundColor: this.colors.primary,
                        borderWidth: 0,
                    },
                    {
                        label: 'Total CU',
                        data: top10.map(ix => ix.totalCU),
                        backgroundColor: this.colors.accent,
                        borderWidth: 0,
                    }
                ]
            };
            this.charts.instructions.update();
        },

        updateFeeChart() {
            const ts = this.feeTimeSeries;
            if (!ts.length) return;
            const labels = ts.map(d => d.label);

            this.charts.fees.data = {
                labels,
                datasets: [
                    {
                        label: 'Base Fees',
                        data: ts.map(d => d.baseFees),
                        backgroundColor: '#6366f1',
                        borderWidth: 0,
                    },
                    {
                        label: 'Priority Fees',
                        data: ts.map(d => d.priorityFees),
                        backgroundColor: '#8b5cf6',
                        borderWidth: 0,
                    },
                    {
                        label: 'Jito Tips',
                        data: ts.map(d => d.tips),
                        backgroundColor: '#f97316',
                        borderWidth: 0,
                    }
                ]
            };
            this.charts.fees.update();
        },
    },
};
</script>

<style scoped>
.dex-logo {
    width: 48px;
    height: 48px;
    border-radius: var(--compass-radius-sm);
}

.badge-program {
    background: rgba(139, 92, 246, 0.1);
    border: 1px solid var(--compass-border);
    border-radius: var(--compass-radius-sm);
    padding: 4px 10px;
    font-size: 0.8rem;
}

.badge-program a {
    text-decoration: none;
}

.badge-program a:hover {
    color: var(--compass-purple) !important;
}

.buy-sell-bar {
    width: 60px;
    height: 6px;
    background: var(--compass-danger);
    border-radius: 3px;
    overflow: hidden;
    display: inline-block;
    vertical-align: middle;
    margin-right: 6px;
}

.buy-segment {
    height: 100%;
    background: var(--compass-success);
    border-radius: 3px 0 0 3px;
}

.token-logo {
    width: 20px;
    height: 20px;
    border-radius: 50%;
}

.token-logo--overlap {
    margin-left: -6px;
}

.pool-pair-logos {
    display: flex;
    align-items: center;
    flex-shrink: 0;
}

.agg-logo {
    width: 20px;
    height: 20px;
    border-radius: 50%;
}

.split-bar {
    display: flex;
    height: 32px;
    border-radius: var(--compass-radius-sm);
    overflow: hidden;
}

.split-segment {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    font-weight: 600;
    color: #fff;
    transition: width 0.3s ease;
}

.split-segment--retail {
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
}

.split-segment--whale {
    background: linear-gradient(135deg, #f59e0b, #ef4444);
}

.fee-summary-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
}

.fee-summary-item {
    padding: 12px;
    border-radius: var(--compass-radius-sm);
    background: rgba(139, 92, 246, 0.05);
    border: 1px solid var(--compass-border);
}

.fee-value {
    font-size: 1.1rem;
    font-weight: 600;
    margin-top: 4px;
}

.whale-retail-split {
    padding: 20px;
}
</style>
